import type { TradingStackNavigationProps } from '@south-street-app/navigation/types';
import type {
  TradeRequestShareDto,
  TradeComponentInputDto,
} from '@utility-nyc/react-query-sdk';

import { useCallback, useEffect, useState } from 'react';

import { Platform, SafeAreaView, StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import {
  getColorTokenValue,
  getSpaceTokenValue,
} from '@utility-nyc/react-native-ui-config';
import { Spacer, useMedia, XStack, YStack } from 'tamagui';
import { useShallow } from 'zustand/react/shallow';

import { Icon, BodyL, BodyXL, HeadingXL } from '@south-street-app/atoms';
import { en_US } from '@south-street-app/configs';
import { useRecentTradeMatch } from '@south-street-app/hooks';
import { ButtonLarge } from '@south-street-app/molecules';
import {
  useGlobalBottomSheetStore,
  useMobileTradeStore,
} from '@south-street-app/stores';
import {
  useGetCurrentEndUser,
  useGetEndUserTradeHistory,
} from '@utility-nyc/react-query-sdk';

const SENTRY_TAG_TYPE = 'screen';
const SENTRY_TAG_CONTEXT = 'TradeTimeoutScreen';

const TIMEOUT_TYPE = {
  timeout: 'Trade timed out',
  rejected: 'Rejected trade',
  canceled: 'Trade cancelled',
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: getColorTokenValue('$mono100'),
  },
  pressStyle: {
    opacity: 0.7,
  },
});

const TradeTimeoutScreen = ({
  route: { params },
}: TradingStackNavigationProps<'TradeTimeout'>) => {
  const { timeoutType } = params;
  const navigation = useNavigation();
  const { desktop } = useMedia();
  const openBottomSheet = useGlobalBottomSheetStore(
    (state) => state.openBottomSheet,
  );

  const {
    proposal,
    createTradeRequest,
    setReceivedQuotedPrice,
    clearFirmEndUsers,
    tradeType,
    shares,
    clearPersistantMessages,
    tradeSubmissionStatus,
    tradeRequestId,
  } = useMobileTradeStore(
    useShallow((state) => ({
      proposal: state.proposal,
      createTradeRequest: state.createTradeRequest,
      setReceivedQuotedPrice: state.setReceivedQuotedPrice,
      tradeType: state.tradeType,
      shares: state.shares,
      clearFirmEndUsers: state.clearFirmEndUsers,
      clearPersistantMessages: state.clearPersistantMessages,
      tradeSubmissionStatus: state.tradeSubmissionStatus,
      tradeRequestId: state.tradeRequestId,
    })),
  );
  const isRejectedTrade = timeoutType === 'rejected';
  const rejectedTradeRequestId = isRejectedTrade ? tradeRequestId : null;
  const [confirmedTradeRequestId, setConfirmedTradeRequestId] = useState<
    string | null
  >(null);
  const rejectionConfirmed =
    !!rejectedTradeRequestId &&
    confirmedTradeRequestId === rejectedTradeRequestId;
  const { data: tradeHistory } = useGetEndUserTradeHistory({
    query: {
      enabled: isRejectedTrade && !!rejectedTradeRequestId,
      refetchOnMount: 'always',
      refetchInterval:
        isRejectedTrade && !rejectionConfirmed ? 1000 : false,
    },
  });

  useEffect(() => {
    if (!isRejectedTrade || !rejectedTradeRequestId || !tradeHistory) {
      return;
    }

    const rejectedTrade = tradeHistory.data.tradeRequests.find(
      (tradeRequest) => tradeRequest.id === rejectedTradeRequestId,
    );

    if (rejectedTrade?.status === 'OFFER_REJECTED') {
      setConfirmedTradeRequestId(rejectedTradeRequestId);
    }
  }, [tradeHistory, isRejectedTrade, rejectedTradeRequestId]);

  const { data } = useGetCurrentEndUser();
  const recentTradeMatch = useRecentTradeMatch(
    data && (!isRejectedTrade || rejectionConfirmed)
      ? {
          components: proposal.components.map(({ product, amount, type }) => ({
            productId: product.id,
            amount,
            type,
          })),
          shares: shares.length ? shares : proposal.shares,
          type: tradeType,
        }
      : null,
  );
  const isMatchingRecentTrade =
    !!recentTradeMatch.tradeRequestId ||
    (recentTradeMatch.retryAfterMs ?? 0) > 0;
  const isTradeStateUnavailable =
    recentTradeMatch.isChecking || recentTradeMatch.isError || !data;

  const reSubmitTrade = useCallback(async () => {
    setReceivedQuotedPrice(false);

    const components: TradeComponentInputDto[] = proposal.components.map(
      (component) => ({
        productId: component.product.id,
        amount: component.amount,
        type: component.type,
      }),
    );

    let tradeSharesToUse: TradeRequestShareDto[];

    if (shares.length > 0) {
      tradeSharesToUse = shares; // Use existing shares if they're populated (firm trades)
    } else if (proposal.shares.length > 0) {
      tradeSharesToUse = proposal.shares; // Use shares from counterproposal if available
    } else {
      tradeSharesToUse = []; // Use empty array if no shares are available as a fallback
    }

    // Stripping out the `associated` field from the shares data
    const sharesData = tradeSharesToUse.map((share) => ({
      amount: share.amount,
      type: share.type,
      associatedId: share.associatedId,
    }));

    try {
      if (data) {
        const sent = await createTradeRequest({
          components,
          type: tradeType,
          shares: sharesData,
          endUserId: data.data.id,
        });

        if (sent) {
          openBottomSheet({
            type: 'requestTrade',
          });
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          type: SENTRY_TAG_TYPE,
          context: SENTRY_TAG_CONTEXT,
          method: 'reSubmitTrade',
        },
      });

      openBottomSheet({
        type: 'backendError',
      });
    }
  }, [
    createTradeRequest,
    proposal.components,
    proposal.shares,
    openBottomSheet,
    shares,
    data,
    setReceivedQuotedPrice,
    tradeType,
  ]);

  const handleGoHome = useCallback(() => {
    clearFirmEndUsers();

    clearPersistantMessages();

    navigation.navigate('HomeTab', { screen: 'TradingScreen' });
  }, [navigation, clearPersistantMessages, clearFirmEndUsers]);

  const handleChatWithTrader = useCallback(() => {
    navigation.navigate('ChatScreen');
  }, [navigation]);

  return (
    <SafeAreaView style={styles.container}>
      <YStack
        paddingTop={'$16'}
        paddingHorizontal={'$4'}
        backgroundColor={'$mono100'}
        justifyContent={'space-between'}
        flex={1}
        {...(desktop && {
          width: 800,
          marginHorizontal: '$auto',
          justifyContent: 'flex-start',
          gap: '$16',
        })}
      >
        <YStack>
          <HeadingXL fontWeight={'$3'} paddingBottom={'$1'}>
            {TIMEOUT_TYPE[timeoutType]}
          </HeadingXL>
          <BodyXL>{en_US.tradeWasNotCompleted}</BodyXL>
          <Spacer height={'$6'} />
          <XStack
            alignItems={'center'}
            backgroundColor={'$mono200'}
            padding={'$5'}
            {...(Platform.OS === 'web' ? { cursor: 'pointer' } : undefined)}
            borderRadius={getSpaceTokenValue('$3')}
            justifyContent={'space-between'}
            pressStyle={styles.pressStyle}
            onPress={handleChatWithTrader}
          >
            <BodyL color={'$brand600'} fontWeight={'$2'}>
              {en_US.chatWithTrader}
            </BodyL>
            <Icon
              iconName={'chevronRightIcon'}
              size={'$6'}
              color={'$brand600'}
            />
          </XStack>
          <YStack />
        </YStack>
        <YStack space={'$2'} paddingBottom={'$3'}>
          <ButtonLarge
            variant={'secondary'}
            color={'$brand500'}
            fontWeight={'$2'}
            disabled={
              tradeSubmissionStatus !== 'idle' ||
              (isRejectedTrade && !rejectionConfirmed) ||
              isTradeStateUnavailable ||
              isMatchingRecentTrade ||
              !data
            }
            onPress={reSubmitTrade}
          >
            {isRejectedTrade
              ? en_US.reSubmitTrade
              : tradeSubmissionStatus !== 'idle'
              ? en_US.requestingTrade
              : isMatchingRecentTrade
              ? en_US.recentIdenticalTrade
              : en_US.reSubmitTrade}
          </ButtonLarge>
          <ButtonLarge variant={'primary'} onPress={handleGoHome}>
            {en_US.goHome}
          </ButtonLarge>
        </YStack>
      </YStack>
    </SafeAreaView>
  );
};

export { TradeTimeoutScreen };
